/**
 * Config-blob creation service (spec §70 §6.1, §4.3)
 *
 * Creates a proper SecureBlob containing /config.json, using the same
 * commit signing primitives as regular workspaces. The MCP server
 * can then open this blob and read /config.json.
 *
 * Uses buildSignedCommit from commitSigning.ts — same server-validated
 * commit format as workspace commits.
 *
 * v2: Uses workspace access tokens (Spec §6.1) for all server communication.
 * Config blobs are bootstrapped on the server before the first dataplane
 * request, and subsequent operations use the session token provider.
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { hkdf as nobleHkdf } from '@noble/hashes/hkdf'
import { WebCryptoProvider } from '../providers'
import { HttpStoreBackend } from '../storeBackends'
import { BlobRepositoryImpl } from '../blobRepository'
import { packFileContent } from '../contentCodec'
import { encryptState, buildAad, type WorkspaceState } from '../stateCodec'
import { buildTreeFromPaths } from '../dirTreeCodec'
import { buildSignedCommit } from '../commitSigning'
import type { McpConfig, McpConfigOwner } from './types'
import { encodeConfigConnectionTokenV2, encodeWorkspaceSecretV2, decodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'
import { createWorkspaceAuthProvider, bootstrapWorkspace } from '@secureblob/sdk/workspaceAuth'
import { createSdk } from '@secureblob/sdk/factory'
import type { WorkspaceAuthProvider } from '@secureblob/sdk/workspaceSecretV2'

// ── Key derivation ─────────────────────────────────────────────

function deriveKey(readKey: string, blobId: string, scope: 'state' | 'file'): Uint8Array {
    let base64 = readKey.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) base64 += '='
    const binary = atob(base64) // throws on invalid base64 — intentional fail-fast
    const readKeyBytes = new Uint8Array([...binary].map(c => c.charCodeAt(0)))
    const salt = new TextEncoder().encode(`secureblob:${blobId}`)
    const info = new TextEncoder().encode(`secureblob-${scope}-v1`)
    return nobleHkdf(nobleSha256, readKeyBytes, salt, info, 32)
}

// ── V2 secret encoding ────────────────────────────────────────

/**
 * Build a workspace-secret-v2 for config-blob transport.
 */
function encodeV2Secret(blobId: string, readKey: string, readerSeed: string, writerSeed: string): string {
    return encodeWorkspaceSecretV2({
        v: 2,
        type: 'workspace-secret',
        blobId,
        readKey,
        readerSeed,
        writerSeed,
    })
}

// ── Helpers ────────────────────────────────────────────────────

function base64UrlDecodeBytes(str: string): Uint8Array {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) base64 += '='
    const binary = atob(base64)
    return new Uint8Array([...binary].map(c => c.charCodeAt(0)))
}

/**
 * Create an auth-aware HTTP backend for config-blob operations.
 * Uses SessionTokenProvider to obtain workspace access tokens
 * via the v2 auth challenge-signing flow (Spec §6.1).
 */
function createAuthRemote(apiBaseUrl: string, v2Secret: string, configBlobId: string): HttpStoreBackend {
    const authProvider = createWorkspaceAuthProvider(apiBaseUrl, v2Secret, configBlobId)
    return new HttpStoreBackend(apiBaseUrl, v2Secret, configBlobId, authProvider ?? undefined)
}

/** Shared logic: encrypt content + state → build signed commit → push */
async function commitConfigBlob(
    apiBaseUrl: string,
    configBlobId: string,
    readKey: string,
    readerSeed: string,
    writerSeedB64: string,
    parents: string[],
    config: McpConfig,
    authProvider?: WorkspaceAuthProvider,
): Promise<void> {
    console.log(`[MCP-ConfigBlob] commitConfigBlob blob=${configBlobId.slice(0, 12)} parents=[${parents.map(p => p.slice(0, 8))}]`)
    const crypto = new WebCryptoProvider()

    const stateKey = deriveKey(readKey, configBlobId, 'state')
    const fileKey = deriveKey(readKey, configBlobId, 'file')

    const v2Secret = encodeV2Secret(configBlobId, readKey, readerSeed, writerSeedB64)
    const remote = authProvider
        ? new HttpStoreBackend(apiBaseUrl, v2Secret, configBlobId, authProvider)
        : createAuthRemote(apiBaseUrl, v2Secret, configBlobId)
    // In-memory local backend — config-blobs are write-once, no persistence
    const local = {
        async load(): Promise<Uint8Array | null> { return null },
        async store(): Promise<void> { },
        async loadBatch(): Promise<Map<string, Uint8Array>> { return new Map() },
        async storeBatch(): Promise<void> { },
    }
    const repository = new BlobRepositoryImpl(local, remote)

    // 1. Encrypt /config.json content
    const configJson = JSON.stringify(config, null, 2)
    const configBytes = new TextEncoder().encode(configJson)

    const fileAad = buildAad(configBlobId, 'file')
    const packed = await packFileContent(configBytes, {
        encryptRawObject: (plaintext) => crypto.encrypt(fileKey, plaintext, fileAad),
        decryptRawObject: (ct) => crypto.decrypt(fileKey, ct, fileAad),
        encryptDeterministic: (pt) => crypto.encryptDeterministic(fileKey, pt, fileAad),
        hasObject: async () => false,
        sha256: (data) => crypto.sha256(data),
    })

    // 2. Build v3 dir tree
    const now = Date.now()
    const dirTreeDeps = { crypto, stateKey, blobId: configBlobId }
    const { rootDirHash, objects: dirObjects } = await buildTreeFromPaths(
        [{
            path: '/config.json',
            entry: {
                kind: 'file' as const,
                mime: 'application/json',
                size: configBytes.byteLength,
                mtime: now,
                ctime: now,
                content: packed.content,
            },
        }],
        dirTreeDeps,
    )

    // 3. Encrypt state (v3 with rootDirHash)
    const state: WorkspaceState = { type: 'secureblob-state-v3', rootDirHash }

    const { ciphertext: stateBytes, hash: stateHash } = await encryptState(state, { crypto, stateKey, blobId: configBlobId })

    // 3. Decode writer seed and build signed commit
    const writerSeed = base64UrlDecodeBytes(writerSeedB64)
    const { commitBytes, commitHash } = await buildSignedCommit({
        blobId: configBlobId,
        parents,
        stateHash,
        writerSeed,
        crypto,
    })

    // 4. Commit transaction (push objects + set head)
    const tx = repository.transaction(configBlobId)

    for (const [hash, bytes] of dirObjects) {
        tx.put(hash, bytes)
    }
    for (const obj of packed.objects) {
        tx.put(obj.hash, obj.bytes)
    }
    tx.put(stateHash, stateBytes)
    tx.put(commitHash, commitBytes)

    const base = parents
    // The server cannot traverse the encrypted state and directory objects during GC.
    const referencedObjects = [stateHash, ...dirObjects.keys(), ...packed.objects.map(obj => obj.hash)]
    const result = await tx.commit({ base, new: commitHash, referencedObjects })

    if (!result.ok) {
        throw new Error(`Config-blob commit failed: ${result.code ?? 'unknown'}`)
    }
}

function hasSameOwner(current: McpConfigOwner | undefined, next: McpConfigOwner): boolean {
    return current?.primaryKid === next.primaryKid
        && current?.displayName === next.displayName
        && current?.emoji === next.emoji
}

// ── Public API ─────────────────────────────────────────────────

/**
 * Read and parse `/config.json` from an MCP config blob.
 * Uses a direct workspace-secret-v2 session instead of re-implementing
 * commit/state parsing in the webapp.
 */
export async function readConfigBlob(
    apiBaseUrl: string,
    configBlobId: string,
    readKey: string,
    readerSeed: string,
    writerSeed: string,
): Promise<McpConfig> {
    const workspaceSecret = encodeV2Secret(configBlobId, readKey, readerSeed, writerSeed)
    const sdk = createSdk()
    const session = await sdk.openWorkspaceDirect({
        apiBaseUrl,
        workspaceSecret,
        options: { persistence: 'memory' },
    })

    try {
        const configText = await session.readText('/config.json')
        if (configText === null) {
            throw new Error('Could not fetch config-blob /config.json')
        }

        const parsed = JSON.parse(configText) as McpConfig
        if (parsed.type !== 'secureblob-mcp-config') {
            throw new Error(`Unexpected config type: ${parsed.type ?? 'unknown'}`)
        }

        return parsed
    } finally {
        session.close()
    }
}

/**
 * Refresh the `owner` block in an existing config blob after profile updates.
 * Returns true when a write was required, false when the blob was already current.
 */
export async function refreshConfigBlobOwner(
    apiBaseUrl: string,
    configBlobId: string,
    readKey: string,
    readerSeed: string,
    writerSeed: string,
    owner: McpConfigOwner,
): Promise<boolean> {
    const config = await readConfigBlob(apiBaseUrl, configBlobId, readKey, readerSeed, writerSeed)
    if (hasSameOwner(config.owner, owner)) {
        return false
    }

    await updateConfigBlob(apiBaseUrl, configBlobId, readKey, readerSeed, writerSeed, {
        ...config,
        owner,
    })
    return true
}

/**
 * Create a config SecureBlob in CryptoBlobStore (genesis commit).
 * Bootstraps the workspace ACL on the server first (Spec §6.1 POST /auth/bootstrap),
 * then commits the config content via the v2 auth transport.
 */
export async function createConfigBlob(
    apiBaseUrl: string,
    configBlobId: string,
    readKey: string,
    readerSeed: string,
    writerSeed: string,
    config: McpConfig,
): Promise<void> {
    // Bootstrap the config blob on the server (register ACL with reader + writer)
    const v2Secret = encodeV2Secret(configBlobId, readKey, readerSeed, writerSeed)
    const decodedSecret = decodeWorkspaceSecretV2(v2Secret)
    if (!decodedSecret) throw new Error('Failed to decode config blob workspace secret')

    console.log(`[MCP-ConfigBlob] Bootstrapping config blob ${configBlobId.slice(0, 12)} on server...`)
    await bootstrapWorkspace(apiBaseUrl, decodedSecret)
    console.log(`[MCP-ConfigBlob] Bootstrap complete`)

    const authProvider = createWorkspaceAuthProvider(apiBaseUrl, v2Secret, configBlobId)
    await commitConfigBlob(apiBaseUrl, configBlobId, readKey, readerSeed, writerSeed, [], config, authProvider ?? undefined)
}

/**
 * Update an existing config SecureBlob with new config content.
 * Fetches current head as parent for CAS update.
 * Uses session token provider for v2 auth transport.
 */
export async function updateConfigBlob(
    apiBaseUrl: string,
    configBlobId: string,
    readKey: string,
    readerSeed: string,
    writerSeed: string,
    config: McpConfig,
): Promise<void> {
    const v2Secret = encodeV2Secret(configBlobId, readKey, readerSeed, writerSeed)
    const authProvider = createWorkspaceAuthProvider(apiBaseUrl, v2Secret, configBlobId)
    const remote = new HttpStoreBackend(apiBaseUrl, v2Secret, configBlobId, authProvider ?? undefined)

    // Fetch current head
    const { heads: currentHeads } = await remote.getHeads(configBlobId)
    if (!currentHeads || currentHeads.length === 0) {
        throw new Error('Config-blob has no head — use createConfigBlob instead')
    }

    await commitConfigBlob(apiBaseUrl, configBlobId, readKey, readerSeed, writerSeed, [currentHeads[0]], config, authProvider ?? undefined)
}

/**
 * Check if a config-blob exists on the server; if not, re-create it (server data loss recovery).
 *
 * Returns true if the blob was re-pushed, false if it already existed.
 */
export async function repushConfigBlobIfMissing(
    apiBaseUrl: string,
    configBlobId: string,
    readKey: string,
    readerSeed: string,
    writerSeed: string,
    config: McpConfig,
): Promise<boolean> {
    const v2Secret = encodeV2Secret(configBlobId, readKey, readerSeed, writerSeed)
    const authProvider = createWorkspaceAuthProvider(apiBaseUrl, v2Secret, configBlobId)
    const remote = new HttpStoreBackend(apiBaseUrl, v2Secret, configBlobId, authProvider ?? undefined)
    console.log(`[MCP-ConfigBlob] repushConfigBlobIfMissing blob=${configBlobId.slice(0, 12)} — checking heads`)

    let currentHeads: string[] = []
    try {
        const { heads } = await getHeadsWithBootstrapRecovery(remote, apiBaseUrl, v2Secret, configBlobId)
        currentHeads = heads ?? []
    } catch {
        // Network error — can't verify, skip re-push to avoid false positives
        return false
    }

    if (currentHeads.length > 0) {
        try {
            await verifyConfigBlobReadable(apiBaseUrl, v2Secret)
            return false
        } catch (error) {
            if (!shouldRepairUnreadableConfigBlob(error)) {
                return false
            }
            console.info(`[MCP] Config-blob ${configBlobId.slice(0, 8)} unreadable — re-writing config on top of current head`)
        }
    } else {
        console.info(`[MCP] Config-blob ${configBlobId.slice(0, 8)} missing on server — re-pushing`)
    }

    // Need to bootstrap again since ACL may also be gone.
    const decodedSecret = decodeWorkspaceSecretV2(v2Secret)
    if (decodedSecret) {
        try {
            await bootstrapWorkspace(apiBaseUrl, decodedSecret)
        } catch {
            // Bootstrap may fail if ACL already exists — that's fine, continue with commit
            console.info(`[MCP-ConfigBlob] Bootstrap skipped (ACL may already exist)`)
        }
    }

    await commitConfigBlob(
        apiBaseUrl,
        configBlobId,
        readKey,
        readerSeed,
        writerSeed,
        currentHeads.length > 0 ? [currentHeads[0]] : [],
        config,
        authProvider ?? undefined,
    )
    return true
}

async function getHeadsWithBootstrapRecovery(
    remote: HttpStoreBackend,
    apiBaseUrl: string,
    v2Secret: string,
    configBlobId: string,
) {
    try {
        return await remote.getHeads(configBlobId)
    } catch (error) {
        if (!shouldBootstrapAfterAuthFailure(error)) throw error
        const decodedSecret = decodeWorkspaceSecretV2(v2Secret)
        if (!decodedSecret) throw error
        try {
            await bootstrapWorkspace(apiBaseUrl, decodedSecret)
        } catch (bootstrapError) {
            if (!(bootstrapError instanceof Error) || !bootstrapError.message.includes('ACL_EXISTS')) {
                throw bootstrapError
            }
        }
        return remote.getHeads(configBlobId)
    }
}

function shouldBootstrapAfterAuthFailure(error: unknown): boolean {
    if (!(error instanceof Error)) return false
    return error.message.includes('ACL_NOT_FOUND')
        || error.message === 'Auth session failed: 404'
        || error.message.includes('Auth session failed: 404 ')
}

async function verifyConfigBlobReadable(apiBaseUrl: string, configBlobSecret: string): Promise<void> {
    const token = encodeConfigConnectionTokenV2({
        v: 2,
        type: 'config-connection-token',
        apiBaseUrl,
        secret: configBlobSecret,
    })
    const sdk = createSdk({ token })
    await sdk.listWorkspaces()
}

function shouldRepairUnreadableConfigBlob(error: unknown): boolean {
    if (hasErrorCode(error, 'CONFIG_BLOB_UNAVAILABLE')) {
        return true
    }
    if (!(error instanceof Error)) {
        return false
    }
    return error.message.includes('Could not fetch config-blob')
        || error.message.includes('Config-blob has no head')
        || error.message.includes('Unexpected config type')
}

function hasErrorCode(error: unknown, expectedCode: string): boolean {
    return typeof error === 'object'
        && error !== null
        && 'code' in error
        && (error as { code?: unknown }).code === expectedCode
}
